import {useEffect} from "react";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface JiraFormProps {
  serverErrors?: ServerErrors;
}

export const JiraForm = ({serverErrors}: JiraFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  const customFieldExample = (
    <Popover id="popover-basic" style={{maxWidth: "40em"}}>
      <Popover.Header as="h3">Example Configuration</Popover.Header>
      <Popover.Body>
        <pre>{`{
  "customfield_10000": "this is a text field",
  "customfield_20000": "this is a multi-line text field. big text.",
  "customfield_30000": "2011-07-13T15:25:00",
  "customfield_40000": {
    "value": "this is a select list value"
  },
  "customfield_50000": {
    "name": "this is a group picker value"
  },
  "customfield_60000": [
    {"value": "this is a multi select list value #1"},
    {"value": "this is a multi select list value #2"}
  ],
}`}</pre>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_email"
            label="API Email Address"
            required={true}
            tooltip="Email of the user whose API token is being used."
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_token"
            label="API Token"
            required={true}
            tooltip="API Token created for the user, or the user's password (self-hosted only)"
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.jira_subdomain"
            label="Jira Subdomain"
            required={true}
            tooltip='The domain your company uses to access Atlassian JIRA/JSD, e.g. "https://your-domain.atlassian.net"'
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.project_key"
            label="Project Key"
            required={true}
            tooltip='The project key of your Service Desk project, shown on the Project Settings > Details page. E.g. "SMPSRVDESK"'
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.labels"
            label="Labels"
            required={false}
            tooltip='A comma separated list of labels attached to each incident created. The "Uptime.com" label any associated check tags are always set'
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.custom_field_id_account_name"
            label="Account Name Custom Field ID"
            required={false}
            tooltip="An optional ID for a custom text field that should receive the account name."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.custom_field_id_check_name"
            label="Check Name Custom Field ID"
            required={false}
            tooltip="An optional ID for a custom text field that should receive the check name."
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.custom_field_id_check_url"
            label="Check URL Custom Field ID"
            required={false}
            tooltip="An optional ID for a custom text field that should receive the check URL."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <TextAreaField
            rows={4}
            name="settings.custom_fields_json"
            label="Additional Custom Fields"
            required={false}
            tooltip="Advanced configuration for custom fields using JSON format."
          />
          <OverlayTrigger trigger="click" placement="right" overlay={customFieldExample}>
            <span>See example configuration</span>
          </OverlayTrigger>
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.alert_resolved_label"
            label="Alert Resolved Label"
            required={false}
            tooltip="When you fill in this field, a label will be added to the Jira issue once the Alert is resolved. Note: Spaces are not supported."
          />
        </div>
      </div>
      <SetupInfo metricsWarning={true}>
        <div className="p-2">
          This integration sends alerts by creating Issues of type &quot;Incident&quot; directly in your Jira Service
          Desk queue.
        </div>
        <div className="p-2">
          <ol className="px-3">
            <li>
              <p className="mb-0">
                <strong>Create your Jira API Token</strong>
              </p>
              <p className="mb-1">
                Log in to Jira and go to&nbsp;
                <a target="_blank" href="https://id.atlassian.com/manage/api-tokens">
                  https://id.atlassian.com/manage/api-tokens
                </a>
                . Click &quot;Create API Token&quot; to generate your token, then enter your email address and the
                provided API token into the corresponding fields of the form above.
              </p>
            </li>
            <li>
              <p className="mb-0">
                <strong>Determine your Jira Service Desk subdomain</strong>
              </p>
              <p className="mb-1">
                Log into Jira Service Desk and copy the beginning part of the address/URL from the top of your browser
                into the <strong>Jira Subdomain</strong> field above. It will look like:
                <code>https://your-domain.atlassian.net</code>
              </p>
            </li>
            <li>
              <p className="mb-0">
                <strong>Determine your Service Desk Project Key</strong>
              </p>
              <p className="mb-1">
                While logged into your Service Desk click &quot;Project Settings&quot; (cog icon) from the menu and then
                choose &quot;Details&quot; from the subsequent menu. Copy the value shown in the <strong>Key</strong>{" "}
                field into <strong>Project Key</strong>
                in the form above. Example value: <code>SMPSRVDESK</code>
              </p>
            </li>
            <li>
              <p className="mb-0">
                <strong>Ensure the &quot;Incident&quot; issue type is enabled</strong>
              </p>
              <p className="mb-1">
                While logged into your Service Desk click &quot;Project Settings&quot; (cog icon) from the menu and then
                choose &quot;Issue Types&quot; from the subsequent menu. If the &quot;Incident&quot; issue type is not
                already included in the current scheme, choose &quot;Actions &gt;&gt; Edit Issue Types&quot; and enable
                the &quot;Incident&quot; type.
              </p>
            </li>
            <li>
              <p className="mb-0">
                <strong>Create Custom Field(s) to receive additional values</strong>
              </p>
              <p className="mb-1">
                Go to your Jira homepage and select &quot;Jira Settings&quot; (cog icon) from the menu. Select
                &quot;Issues&quot; and then &quot;Custom Fields&quot; from the next two menus. Click &quot;Add custom
                field&quot; and follow the dialogs to create your field. Finally &quot;Edit&quot; your new field from
                the field list shown, and the
                <strong>Custom Field ID</strong> will be the last number in your browser's address/URL bar.
                <br />
                E.g.:{" "}
                <code>
                  https://your-domain.atlassian.net/.../?customFieldId=<strong>10042</strong>
                </code>
              </p>
            </li>
          </ol>
        </div>
        <div className="p-2">
          <a
            href="https://support.uptime.com/hc/en-us/articles/360002593379-Configuring-Jira-Service-Desk"
            target="_blank"
          >
            View {IntegrationModuleInfo[IntegrationModules.JIRA].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
